import React, { useState } from 'react';
import { Box, Link, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import Modal from '../components/Modal';
import PageContainer from '../layouts/PageContainer';
import { useV2Session } from '../state/SessionProvider';
import { pilotAuthConfirmDeleteAccount, pilotAuthSendDeleteOtp } from '../services/auth';
import { V2ApiError } from '../services/client';
import { paths } from '../config/paths';

function normalizeMobile(raw) {
  return String(raw || '').replace(/\D/g, '').slice(-10);
}

function isValidIndianMobile(digits) {
  return /^[6789]\d{9}$/.test(digits);
}

export default function DeleteAccount() {
  const { loggedIn, mobile: sessionMobile, logout } = useV2Session();
  const navigate = useNavigate();
  const lockedMobile = loggedIn ? normalizeMobile(sessionMobile) : '';

  const [step, setStep] = useState('phone');
  const [mobile, setMobile] = useState(lockedMobile);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const digits = lockedMobile || normalizeMobile(mobile);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidIndianMobile(digits)) {
      setError('Enter a valid 10-digit Indian mobile number.');
      return;
    }
    setSubmitting(true);
    try {
      await pilotAuthSendDeleteOtp({ mobile: digits });
      setStep('otp');
      setOtp('');
    } catch (err) {
      const msg = err instanceof V2ApiError ? err.message : 'Something went wrong. Try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const openConfirm = (e) => {
    e.preventDefault();
    setError('');
    const code = String(otp).trim();
    if (!/^\d{4,8}$/.test(code)) {
      setError('Enter the OTP you received.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setError('');
    setSubmitting(true);
    try {
      await pilotAuthConfirmDeleteAccount({ mobile: digits, otp: String(otp).trim() });
      setConfirmOpen(false);
      const deletedOwnSession = loggedIn && normalizeMobile(sessionMobile) === digits;
      if (deletedOwnSession) {
        await logout();
        navigate(paths.home, { replace: true });
        return;
      }
      setDeleted(true);
    } catch (err) {
      const msg = err instanceof V2ApiError ? err.message : 'Could not delete the account.';
      setError(msg);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToPhone = () => {
    if (lockedMobile) return;
    setStep('phone');
    setOtp('');
    setError('');
    setConfirmOpen(false);
  };

  if (deleted) {
    return (
      <PageContainer title="Delete account">
        <Typography variant="body2" color="text.secondary">
          The account for +91 {digits} has been deleted.
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Delete account">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {step === 'phone'
          ? 'We’ll send a one-time code to confirm this is your number. Deleting your account will permanently remove your profile.'
          : `Enter the code sent to +91 ${digits}, then confirm deletion.`}
      </Typography>

      {step === 'phone' ? (
        <Box component="form" onSubmit={handleRequestOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="10-digit number"
            value={lockedMobile || mobile}
            onChange={(ev) => setMobile(ev.target.value)}
            disabled={Boolean(lockedMobile)}
            error={Boolean(error)}
            helperText={error || undefined}
            fullWidth
          />
          <Button type="submit" fullWidth size="large" disabled={submitting} sx={{ borderRadius: '5px' }}>
            {submitting ? 'Sending code…' : 'Send OTP'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={openConfirm} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="OTP"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Enter OTP"
            value={otp}
            onChange={(ev) => setOtp(ev.target.value.replace(/\D/g, '').slice(0, 8))}
            error={Boolean(error)}
            helperText={error || undefined}
            fullWidth
            autoFocus
          />
          <Button type="submit" fullWidth size="large" color="error" disabled={submitting} sx={{ borderRadius: '5px' }}>
            Continue
          </Button>
          {lockedMobile ? null : (
            <Typography variant="body2" color="text.secondary">
              <Link component="button" type="button" underline="hover" onClick={goBackToPhone} sx={{ cursor: 'pointer' }}>
                Change number
              </Link>
            </Typography>
          )}
        </Box>
      )}

      <Modal open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} title="Delete account">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes the account for +91 {digits}. You can sign up again later with the same number.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              sx={{ borderRadius: '5px' }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              fullWidth
              color="error"
              onClick={handleConfirmDelete}
              disabled={submitting}
              sx={{ borderRadius: '5px' }}
            >
              {submitting ? 'Deleting…' : 'Delete account'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </PageContainer>
  );
}
